import type { ApiSchemas } from '@rental/contracts/client'
import { format } from '@rental/i18n'
import { Button } from '@rental/ui'
import { ferryWords, placeName } from '../components/BookingItem'
import { type ChipTone, StatusChip } from '../components/StatusChip'
import { NamedFor } from '../components/ui'
import { useLocale } from '../i18n'
import { type Trip, tripFerry, tripPlace } from './trips'

type BookingRow = ApiSchemas['StaffBookingRow']

/**
 * S9 (LOC-5, J8): one trip's parts, said the same way on Today and on its
 * booking's page.
 */

/** A trip named as the booking's own price lines name it: "Consegna: Lacco Ameno". */
export function useTripWords(): (leg: Trip['leg'], booking: BookingRow) => string {
  const { locale, messages: m } = useLocale()
  return (leg, booking) =>
    format(leg === 'delivery' ? m.quoteLines.delivery : m.quoteLines.collection, {
      name: placeName(tripPlace(leg, booking), locale),
    })
}

/** Nobody on it yet is work for the office; done is nothing left to do. */
const TONE: Readonly<Record<Trip['state'], ChipTone>> = {
  to_assign: 'warning',
  assigned: 'info',
  done: 'neutral',
}

/** Where the trip stands, who drives it ("Tuo" for the person asking), and whether it is late. */
export function TripChips({ trip }: { trip: Trip }) {
  const { messages: m } = useLocale()
  const copy = m.trips
  return (
    <>
      <StatusChip tone={TONE[trip.state]}>
        {trip.state === 'assigned'
          ? trip.mine
            ? copy.mine
            : format(copy.state.assigned, { name: trip.task?.assignedTo?.displayName ?? '' })
          : copy.state[trip.state]}
      </StatusChip>
      {/* Due already and still to be driven: red, as in Today's attention bar. */}
      {trip.late ? <StatusChip tone="danger">{m.today.late}</StatusChip> : null}
    </>
  )
}

/** Whether the trip has any of its facts to say: a ferry, a meeting point, a note. */
export function hasTripFacts(trip: Trip, booking: BookingRow): boolean {
  return tripFerry(trip, booking) !== null || !!trip.task?.meetingPoint || !!trip.task?.note
}

/** The ferry it meets, where the customer is met, and the office's note: those it has. */
export function TripFacts({ trip, booking }: { trip: Trip; booking: BookingRow }) {
  const { locale, messages: m } = useLocale()
  const copy = m.trips
  const ferry = tripFerry(trip, booking)
  const { task } = trip
  return (
    <>
      {ferry ? <span>{ferryWords(ferry, locale, m)}</span> : null}
      {task?.meetingPoint ? (
        <span className="wrap-anywhere">
          {format(copy.meetingPoint, { place: task.meetingPoint })}
        </span>
      ) : null}
      {task?.note ? (
        <span className="wrap-anywhere">{format(copy.note, { note: task.note })}</span>
      ) : null}
    </>
  )
}

/** "Assegna" while nobody drives it, "Modifica" once someone does; named for its trip. */
export function TripEditButton({
  trip,
  booking,
  onEdit,
}: {
  trip: Trip
  booking: BookingRow
  onEdit: () => void
}) {
  const { messages: m } = useLocale()
  const tripWords = useTripWords()
  return (
    <div>
      <Button variant="outline" className="rounded-sm" onClick={onEdit}>
        {trip.task?.assignedTo ? m.trips.edit : m.trips.assign}
        <NamedFor name={`${booking.code}, ${tripWords(trip.leg, booking)}`} />
      </Button>
    </div>
  )
}
